import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboard, useUsers } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { Chart } from '@/components/Chart';
import { Avatar, CardSkeleton, ErrorState, ProgressBar, SectionHead, StatLine } from '@/components/ui';
import { IconClock, IconTrophy } from '@/components/Icons';
import { CommissionCard } from '@/components/commission/CommissionCard';
import { CryptoAgendaCard } from '@/components/crypto/CryptoAgendaCard';
import { GasCard } from '@/components/gas/GasCard';
import { ReceiptDetailModal } from '@/components/receipts/ReceiptDetailModal';
import { TransferBadge } from '@/components/receipts/TransferBadge';
import {
  formatBRL, formatBRLShort, formatDate, formatNumber, formatPercent, LEVERAGE_LABELS, LEVERAGE_LONG_LABELS,
} from '@/lib/format';
import { PendingDepositsAlert } from '@/components/receipts/PendingDepositsAlert';
import { countsAsSold, hasOwnValue, notSoldHint, ownValueOf, RECEIPT_TYPES } from '@/lib/receipts';
import type { Receipt } from '@/lib/types';
import { CURRENT_YEAR, YEAR_OPTIONS } from '@/lib/years';

/**
 * Painel do ano, pelos comprovantes. O administrador vê a operação inteira
 * (ou filtra por membro); o membro vê só os próprios números.
 */
export function DashboardPage() {
  const { isAdmin } = useAuth();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [memberId, setMemberId] = useState('');
  const [leverageType, setLeverageType] = useState('');
  // Sem escolha, o servidor usa o mês corrente (ou dezembro, num ano passado).
  const [commissionMonth, setCommissionMonth] = useState<number | undefined>(undefined);
  const [selected, setSelected] = useState<Receipt | null>(null);

  const { data: members } = useUsers({}, { enabled: isAdmin });
  const { data, isLoading, isPlaceholderData, error } = useDashboard({
    year,
    memberId: memberId || undefined,
    leverageType: leverageType || undefined,
    month: commissionMonth,
  });

  const monthly = data?.monthly ?? [];
  const labels = useMemo(() => monthly.map((point) => point.monthShort), [monthly]);
  const values = useMemo(() => monthly.map((point) => point.soldTotal), [monthly]);
  const targets = useMemo(() => monthly.map((point) => point.target), [monthly]);

  // Visão de uma pessoa só: o próprio membro, ou o filtro do administrador.
  const personal = Boolean(data?.scope.memberId);
  const topMembers = (data?.memberRanking ?? []).filter((member) => member.soldTotal > 0).slice(0, 5);
  const maxMemberTotal = Math.max(1, ...topMembers.map((member) => member.soldTotal));

  if (error) return <ErrorState message={(error as Error).message} />;

  return (
    <>
      <h1 className="page-title">{isAdmin ? 'Painel geral' : 'Meu painel'}</h1>

      <PendingDepositsAlert />

      <div className="toolbar">
        <select className="inp" value={year} onChange={(event) => {
            setYear(Number(event.target.value));
            setCommissionMonth(undefined);
          }}
          aria-label="Ano"
        >
          {YEAR_OPTIONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>

        {isAdmin ? (
          <select className="inp" value={memberId} onChange={(event) => setMemberId(event.target.value)} aria-label="Membro">
            <option value="">Toda a operação</option>
            {members?.map((member) => (
              <option key={member.id} value={member.id}>{member.name}</option>
            ))}
          </select>
        ) : null}

        <select className="inp" value={leverageType} onChange={(event) => setLeverageType(event.target.value)} aria-label="Tipo">
          <option value="">Todos os tipos</option>
          {RECEIPT_TYPES.map((type) => <option key={type} value={type}>{LEVERAGE_LONG_LABELS[type]}</option>)}
        </select>

        <div className="spacer" />

        {data ? (
          <span className="chip">
            <IconClock />
            Hoje: <strong className="num">{formatBRLShort(data.today.soldTotal)}</strong>
            <span className="muted">
              · {data.today.receiptCount} {data.today.receiptCount === 1 ? 'comprovante' : 'comprovantes'}
            </span>
          </span>
        ) : null}
      </div>

      {isLoading || !data ? (
        <div className="grid grid2"><CardSkeleton height={160} /><CardSkeleton height={160} /></div>
      ) : (
        // Enquanto o novo filtro carrega, os números anteriores ficam na tela, esmaecidos.
        <div className={`metrics-body${isPlaceholderData ? ' is-stale' : ''}`} aria-busy={isPlaceholderData}>
          <section>
            <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
              {personal && isAdmin ? `Vendido por ${data.scope.memberName} em ${data.year}` : `Vendido em ${data.year}`}
              {data.leverageType ? ` · só ${LEVERAGE_LONG_LABELS[data.leverageType]?.toLowerCase()}` : ''}
            </div>
            <div
              className="display num"
              style={{ fontSize: 'clamp(34px, 6vw, 52px)', fontWeight: 600, color: 'var(--emphasis)', lineHeight: 1 }}
            >
              {formatBRL(data.totals.soldTotal)}
            </div>

            {/* Na operação, a meta dela; no painel de uma pessoa, a meta mensal individual (× 12). */}
            {data.totals.annualTarget > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, maxWidth: 460 }}>
                <div style={{ flex: 1 }}>
                  <ProgressBar value={data.totals.soldTotal} max={data.totals.annualTarget} />
                </div>
                <span className="num muted nowrap" style={{ fontSize: 12.5 }}>
                  {formatPercent(data.totals.targetProgress)} da meta de {formatBRLShort(data.totals.annualTarget)}
                </span>
              </div>
            ) : null}

            <div className={data.leverageType === 'SALE' ? 'metric-row' : 'metric-row cols-5'} style={{ marginTop: 30 }}>
              {/* Só vendas: depósito, alavancagem e repasse ficam de fora. */}
              {data.leverageType === 'SALE' ? null : (
                <>
                  <StatLine label="Depositado" value={formatBRLShort(data.totals.depositTotal)} />
                  <StatLine label="Alavancagem buscada" value={formatBRLShort(data.totals.leverageTotal)} />
                  <StatLine label="Repasse puxado" value={formatBRLShort(data.totals.transferTotal)} color="var(--success)" />
                  <StatLine
                    label="Repasses pendentes"
                    value={formatNumber(data.totals.pendingTransfers)}
                    color={data.totals.pendingTransfers > 0 ? 'var(--warning)' : undefined}
                    sub={data.totals.pendingTransfers > 0 ? 'comprovantes aguardando o repasse' : 'nenhum no ano'}
                  />
                </>
              )}
              <StatLine
                label="Comprovantes"
                value={formatNumber(data.totals.receiptCount)}
                sub={`Ticket médio ${formatBRLShort(data.totals.averageTicket)}`}
              />
            </div>
          </section>

          {/* O que cobrar hoje vem antes dos números do mês: é ação, não leitura. */}
          <CryptoAgendaCard showMember={isAdmin} />

          <CommissionCard
            commission={data.commission}
            year={data.year}
            personal={personal}
            leverageType={data.leverageType}
            selectedMonth={commissionMonth}
            onMonthChange={setCommissionMonth}
          />

          {/* O gás é à parte: segue o ano e o membro, mas não o tipo, e não soma em nada acima. */}
          <GasCard year={data.year} memberId={memberId || undefined} onSelect={setSelected} />

          <section style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <SectionHead title="Vendido por mês" />
              {data.totals.annualTarget > 0 ? (
                <span className="muted" style={{ fontSize: 12 }}>linha tracejada = meta mensal</span>
              ) : null}
            </div>
            <Chart
              type="bar"
              labels={labels}
              data={values}
              target={targets}
              ariaLabel={`Gráfico do valor vendido por mês em ${data.year}`}
            />
          </section>

          {/* No monitor largo, ranking e últimos comprovantes dividem a linha. */}
          <div className="dash-lower">
            <div className={`dash-lower-grid${personal ? '' : ' is-split'}`}>
              {!personal ? (
                <section>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, rowGap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                    <SectionHead title="Ranking de membros — valor vendido" />
                    <div className="spacer" />
                    <Link to="/ranking" className="btn btn-sm">
                      <IconTrophy size={13} />
                      Ver ranking completo
                    </Link>
                  </div>

                  {topMembers.length === 0 ? (
                    <div className="empty">Nenhum comprovante neste ano.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {topMembers.map((member) => (
                        <div key={member.id} className="lead-row" data-rank={member.position}>
                          <span className="display num lead-rank">{member.position}</span>
                          <Avatar name={member.name} color={member.avatarColor} photo={member.photoUrl} size={26} />
                          <span className="truncate lead-row-name">{member.name}</span>
                          <div className="lead-row-bar">
                            <div className="bar-track" style={{ height: 4 }}>
                              <div
                                className="bar-fill"
                                style={{
                                  width: `${(member.soldTotal / maxMemberTotal) * 100}%`,
                                  background: member.position === 1 ? 'var(--primary)' : 'var(--border)',
                                }}
                              />
                            </div>
                          </div>
                          <span
                            className="num right nowrap lead-row-value"
                            style={{ color: member.position === 1 ? 'var(--emphasis)' : 'var(--foreground)' }}
                          >
                            {formatBRLShort(member.soldTotal)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              ) : null}

              <RecentReceipts receipts={data.recentReceipts} personal={personal} onSelect={setSelected} />
            </div>
          </div>
        </div>
      )}

      {selected ? <ReceiptDetailModal receipt={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
}

interface RecentReceiptsProps {
  receipts: Receipt[];
  /** Painel de uma pessoa: a coluna do membro sai. */
  personal: boolean;
  onSelect: (receipt: Receipt) => void;
}

/** Os últimos comprovantes do ano; no celular, cada um vira um cartão. */
function RecentReceipts({ receipts, personal, onSelect }: RecentReceiptsProps) {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, rowGap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <SectionHead title="Últimos comprovantes" />
        <div className="spacer" />
        <Link to="/comprovantes" className="btn btn-sm">Ver todos</Link>
      </div>
      {receipts.length === 0 ? (
        <div className="empty">
          Nenhum comprovante anexado ainda.{' '}
          <Link to="/comprovantes" className="emphasis" style={{ fontWeight: 500 }}>Anexar o primeiro</Link>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-cards">
            <thead>
              <tr>
                <th>Quando</th>
                {personal ? null : <th>Membro</th>}
                <th>Produto</th>
                <th>Tipo</th>
                <th className="right">Valor</th>
                <th>Repasse</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((receipt) => (
                <tr key={receipt.id} className="row-hover row-click" onClick={() => onSelect(receipt)}>
                  <td className="num nowrap" data-label="Quando">
                    {formatDate(receipt.date)} <span className="muted">{receipt.time}</span>
                  </td>
                  {personal ? null : <td className="nowrap" data-label="Membro">{receipt.memberName ?? '—'}</td>}
                  <td className="cell-main">
                    <button
                      type="button"
                      className="link-btn truncate"
                      style={{ display: 'block', maxWidth: 260, fontWeight: 500 }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(receipt);
                      }}
                    >
                      {receipt.description}
                    </button>
                  </td>
                  <td data-label="Tipo">
                    {receipt.leverageType ? (
                      <span className="chip">{LEVERAGE_LABELS[receipt.leverageType]}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {countsAsSold(receipt.leverageType) ? (
                    <td className="num right emphasis nowrap cell-key">{formatBRLShort(receipt.soldAmount)}</td>
                  ) : hasOwnValue(receipt.leverageType) ? (
                    <td className="num right nowrap cell-key" title={notSoldHint(receipt.leverageType)}>{formatBRLShort(ownValueOf(receipt))}</td>
                  ) : (
                    <td className="num right muted nowrap cell-key" title={notSoldHint(receipt.leverageType)}>—</td>
                  )}
                  <td data-label="Repasse">
                    <TransferBadge receipt={receipt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
